import type { HseReportType, TaskItem, TaskKind } from '@felix/domain';
import { CASH_SECTION_PATHS } from '../cash/CashScreen.js';
import { issuancePath } from '../compliance/IssuanceApproval.js';
import { sectionHref } from '../documents/library.js';
import { EMPTY_HISTORY_FILTER, historySearch } from '../forms/history.js';
import { FORM_HISTORY_PATH, recordPath } from '../forms/record-model.js';
import { HSEQ_PATH, hsePath } from '../hse/routes.js';
import { pmsBellPath } from '../pms/PmsBell.js';
import { purchaseOrderPath } from '../purchasing/orders.js';
import { requisitionPath } from '../purchasing/requisitions.js';
import { supplierAccountPath } from '../purchasing/supplier-account.js';
import { supplierProfilePath } from '../purchasing/supplier-profile.js';
import { type ReturnedModule, type ReturnedVariant, returnedPath } from '../returned/Returned.js';

/**
 * Panoda iş satırının açtığı ekran (ADR 0156 "Yollar"): yollar arayüzde, mevcut yardımcılarla
 * hesaplanır; sunucu URL üretmez. Satır kaydın kendi ekranını açar, karar orada verilir (#2240).
 */

const RISK_ASSESSMENT_PATH = '/compliance/risk-assessment';
const ROB_RECONCILIATION_PATH = '/fuel/rob-reconciliation';
const SUPPLIERS_PATH = '/purchasing/suppliers';
const PMS_PLAN_PATH = '/pms/plan';
const VESSEL_CERTIFICATES_PATH = '/compliance/vessel-certificates';

/** PMS satırının varyantından onay kayıt türü (zil yolunun girdisi). */
const PMS_RECORD_TYPES: Readonly<Record<string, string>> = {
  work_record: 'pms_work_record',
  postponement: 'postponement_request',
  interval_change: 'maintenance_interval_change',
  defect: 'defect_report',
  breakdown: 'breakdown_report',
};

const HSE_TYPES: readonly HseReportType[] = [
  'non_conformity',
  'deficiency',
  'near_miss',
  'accident',
];

function query(path: string, params: Record<string, string | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) search.set(key, value);
  }
  const text = search.toString();
  return text ? `${path}?${text}` : path;
}

/** ISM form onay bekleyenleri: Form History'nin "Awaiting my approval" süzgeci. */
export const AWAITING_FORMS_PATH = `${FORM_HISTORY_PATH}${historySearch({
  ...EMPTY_HISTORY_FILTER,
  awaiting: true,
})}`;

/**
 * ISM formda sırası gelmemiş adımlarım: Form History'nin `awaiting=upcoming` süzgeci (panonun
 * "+N sırada" bağlantısı; ADR 0156 §1). "Awaiting my approval" bu kayıtları vermez.
 */
export const UPCOMING_FORMS_PATH = `${FORM_HISTORY_PATH}${historySearch({
  ...EMPTY_HISTORY_FILTER,
  upcoming: true,
})}`;

/** Satırın ekranı. */
export function taskPath(kind: TaskKind, item: TaskItem): string {
  const vessel = item.vesselId;
  switch (kind) {
    case 'returned':
      return returnedPath({
        module: item.module as ReturnedModule,
        variant: item.variant as ReturnedVariant,
        recordId: item.recordId,
        targetId: item.targetId ?? item.recordId,
      });
    case 'ctm_decision':
    case 'ctm_transfer':
      // Bekleyen ve onaylı CTM'ler aynı ekranda (yeni derin bağlantı)
      return query(CASH_SECTION_PATHS['ctm-approvals'], { vessel, id: item.recordId });
    case 'cash_review':
      return query(CASH_SECTION_PATHS.reconciliation, { vessel, id: item.recordId });
    case 'requisition_decision':
    case 'requisition_processing':
      return requisitionPath({ view: 'detail', id: item.recordId });
    case 'po_decision':
      return purchaseOrderPath({ view: 'detail', id: item.recordId, tab: 'items' });
    case 'invoice_review':
      return supplierAccountPath(item.supplierId ?? '', 'invoices');
    case 'payment_approval':
      return supplierAccountPath(item.supplierId ?? '', 'payments');
    case 'supplier_bank_review':
      return supplierProfilePath(item.supplierId ?? '');
    case 'pms_decision':
      return pmsBellPath(
        {
          recordType: PMS_RECORD_TYPES[item.variant ?? ''] ?? 'pms_work_record',
          recordId: item.recordId,
          vesselId: vessel ?? '',
          workOrderId: item.targetId ?? null,
        },
        'web',
      );
    case 'fuel_adjustment_decision':
      return query(ROB_RECONCILIATION_PATH, { vessel, adjustment: item.recordId });
    case 'certificate_issuance':
      return issuancePath({ id: item.recordId, vesselId: vessel ?? '' });
    case 'hse_decision': {
      const type = HSE_TYPES.find((t) => t === item.variant) ?? null;
      return hsePath(type, { view: 'detail', id: item.recordId, tab: null });
    }
    case 'risk_decision':
      return query(RISK_ASSESSMENT_PATH, { vessel, id: item.recordId });
    case 'document_revision':
      return sectionHref(item.module === 'manuals' ? 'manuals' : 'ism', { doc: item.recordId });
    case 'ism_form_decision':
      return recordPath(item.recordId);
  }
}

/** "Tümünü gör": modülün liste ya da onay sayfası; iade edilenlerin listesi yok. */
export function taskListPath(kind: TaskKind, items: readonly TaskItem[]): string | null {
  switch (kind) {
    case 'returned':
      return null;
    case 'ctm_decision':
    case 'ctm_transfer':
      return CASH_SECTION_PATHS['ctm-approvals'];
    case 'cash_review':
      return CASH_SECTION_PATHS.reconciliation;
    case 'requisition_decision':
    case 'requisition_processing':
      return requisitionPath({ view: 'list' });
    case 'po_decision':
      return purchaseOrderPath({ view: 'list' });
    case 'invoice_review':
    case 'payment_approval':
    case 'supplier_bank_review':
      return SUPPLIERS_PATH;
    case 'pms_decision':
      return PMS_PLAN_PATH;
    case 'fuel_adjustment_decision':
      return ROB_RECONCILIATION_PATH;
    case 'certificate_issuance':
      return VESSEL_CERTIFICATES_PATH;
    case 'hse_decision':
      return HSEQ_PATH;
    case 'risk_decision':
      return RISK_ASSESSMENT_PATH;
    case 'document_revision':
      return sectionHref(items[0]?.module === 'manuals' ? 'manuals' : 'ism');
    case 'ism_form_decision':
      return AWAITING_FORMS_PATH;
  }
}
